import { useState } from 'react';
import { SafeDatabaseUser } from '../types/types';
import * as twoFactorService from '../services/twoFactorService';

interface Use2FASetupReturn {
  loading: boolean;
  error: string | null;
  step: 'initial' | 'verify';
  code: string;
  setCode: (code: string) => void;
  handleInitiateSetup: () => Promise<void>;
  handleVerifyCode: () => Promise<SafeDatabaseUser | undefined>;
}

/**
 * Hook for handling the 2FA setup UI flow
 *
 * This hook manages the state and actions for the two-factor authentication setup process,
 * while delegating the actual API calls to the twoFactorService.
 */
const use2FASetup = (user: SafeDatabaseUser): Use2FASetupReturn => {
  const [step, setStep] = useState<'initial' | 'verify'>('initial');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string>('');

  // Handle the initial setup request
  const handleInitiateSetup = async (): Promise<void> => {
    if (!user.email) {
      setError('Email is required for 2FA setup');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await twoFactorService.initiateSetup(user.username, user.email);
      setStep('verify');
    } catch (err) {
      setError('Failed to initiate 2FA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle code verification
  const handleVerifyCode = async (): Promise<SafeDatabaseUser | undefined> => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return undefined;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedUser = await twoFactorService.verifySetupCode(user.username, code);
      return updatedUser;
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to verify code. Please try again.';
      setError(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    step,
    code,
    setCode,
    handleInitiateSetup,
    handleVerifyCode,
  };
};

export default use2FASetup;
